// Builder state: single source of truth for the in-progress strategy. Sections of the
// builder mutate one slice at a time without re-rendering each other unnecessarily.
//
// Defaults pre-fill the asset/indicator side (BTC perps 1m, RSI(14)) so the user lands ready
// to scan. Condition indices are deliberately EMPTY: in the scan-first builder they are only
// ever set by selecting a ranked candidate in the Discover step. Seeding them would let a
// strategy be saved with conditions the user never chose.

import { createContext, useContext, useState, useCallback, type Dispatch, type ReactNode, type SetStateAction } from "react";
import type { CandleInterval, ExitMode, IndicatorSpecDto, MarketType, RiskSettingsDto } from "../api/types";

export interface BuilderChannelDraft {
  type: "webhook";
  url: string;
  enabled: boolean;
}

export interface BuilderDraft {
  name: string;
  coin: string;
  interval: CandleInterval;
  marketType: MarketType;
  indicatorSpecs: IndicatorSpecDto[];
  longCondIndices: number[];
  shortCondIndices: number[];
  // Phase A: separate exit-condition sets. Empty arrays mean "no exit-condition triggers
  // for this direction" — exits then fall back to the configured ExitMode (bars / indicator
  // / reverse). Setting any indices lets the engine and the live evaluator emit dedicated
  // exit-long / exit-short alerts when ALL the ticked conditions fire on the same closed bar.
  exitLongCondIndices: number[];
  exitShortCondIndices: number[];
  exitMode: ExitMode;
  forwardBars: number;
  useAdvanced: boolean;
  usePriceAction: boolean;
  channels: BuilderChannelDraft[];
  // Phase 12 risk exits.
  riskSettings?: RiskSettingsDto;
}

// Defaults pre-fill so a single click after page-load yields a working strategy.
// The webhook URL is filled by AppShell using URLS.testUi (which is protocol-aware),
// so don't hard-code it here.
export const DEFAULT_DRAFT: BuilderDraft = {
  name: "MVP smoke RSI long+short on BTC 1m",
  coin: "BTC",
  interval: "1m",
  marketType: "perps",
  indicatorSpecs: [{ instanceKey: "rsi_0", baseId: "rsi", params: { period: 14 } }],
  longCondIndices: [],
  shortCondIndices: [],
  exitLongCondIndices: [],
  exitShortCondIndices: [],
  // Exits are auto-discovered (scan) or hand-picked (Manual); there's no exit-rule selector.
  // "indicator" mode means the runtime exit is the discovered/explicit conditions (OR) backstopped
  // by the engine's max-hold cap — never a fixed bars timeout or stop-and-reverse.
  exitMode: "indicator",
  forwardBars: 5,
  useAdvanced: false,
  usePriceAction: false,
  channels: [],
};

interface BuilderStateContextValue {
  draft: BuilderDraft;
  // Full React setter (accepts a value OR a functional updater) so sections that merge a
  // nested slice (e.g. RiskSection) can read the LATEST draft and not a stale closure.
  setDraft: Dispatch<SetStateAction<BuilderDraft>>;
  patch: (changes: Partial<BuilderDraft>) => void;
  reset: () => void;
}

const BuilderStateContext = createContext<BuilderStateContextValue | null>(null);

export function BuilderStateProvider({ children, initial }: { children: ReactNode; initial?: BuilderDraft }) {
  const [draft, setDraft] = useState<BuilderDraft>(initial ?? DEFAULT_DRAFT);
  const patch = useCallback((changes: Partial<BuilderDraft>) => {
    setDraft((prev) => {
      const next = { ...prev, ...changes };
      // Condition indices are POSITIONAL into the flat condition list the scanner builds from the
      // indicator set + market context. If any of those inputs change, a previously picked
      // candidate's indices now point at DIFFERENT conditions — a silently-wrong strategy. Clear
      // them so the user must re-scan and re-pick (WizardShell's Discover gate re-locks in step).
      const invalidatesConditions =
        ("coin" in changes && !Object.is(prev.coin, next.coin)) ||
        ("interval" in changes && !Object.is(prev.interval, next.interval)) ||
        ("marketType" in changes && !Object.is(prev.marketType, next.marketType)) ||
        ("indicatorSpecs" in changes && !Object.is(prev.indicatorSpecs, next.indicatorSpecs)) ||
        ("useAdvanced" in changes && !Object.is(prev.useAdvanced, next.useAdvanced)) ||
        ("usePriceAction" in changes && !Object.is(prev.usePriceAction, next.usePriceAction));
      if (
        invalidatesConditions &&
        (next.longCondIndices.length > 0 || next.shortCondIndices.length > 0 ||
          next.exitLongCondIndices.length > 0 || next.exitShortCondIndices.length > 0)
      ) {
        next.longCondIndices = [];
        next.shortCondIndices = [];
        next.exitLongCondIndices = [];
        next.exitShortCondIndices = [];
      }
      return next;
    });
  }, []);
  const reset = useCallback(() => setDraft(DEFAULT_DRAFT), []);
  return (
    <BuilderStateContext.Provider value={{ draft, setDraft, patch, reset }}>
      {children}
    </BuilderStateContext.Provider>
  );
}

export function useBuilderState(): BuilderStateContextValue {
  const ctx = useContext(BuilderStateContext);
  if (!ctx) throw new Error("useBuilderState must be used inside BuilderStateProvider");
  return ctx;
}
